<template>
  <div>
    <!-- News Detail Header -->
    <v-container fluid class="pa-0 news-detail-header">
      <v-row no-gutters>
        <v-col cols="12">
          <v-card class="rounded-0 overflow-hidden h-full">
            <v-img
              :src="newsImage"
              height="400"
              class="h-full"
              gradient="to bottom, rgba(0,0,0,.2), rgba(0,0,0,.8)"
            >
              <div class="news-detail-header-content">
                <v-breadcrumbs :items="breadcrumbs" class="mb-4">
                  <template v-slot:item="{ item }">
                    <v-breadcrumbs-item :href="item.href" v-text="item.title" />
                  </template>
                </v-breadcrumbs>
                <v-card-title class="text-white text-h3 font-bold mb-2" v-html="newsTitle"></v-card-title>
                <div class="date-category">
                  <span class="text-white opacity-90">{{ formattedDate }}</span>
                  <span class="text-white opacity-90 mx-2">•</span>
                  <span class="text-white opacity-90">{{ newsCategory }}</span>
                </div>
              </div>
            </v-img>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- News Detail Content -->
    <v-container class="py-12">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="news-detail-content" v-html="newsContent"></div>

          <!-- Back to News Button -->
          <div class="back-to-news mt-12">
            <v-btn
              variant="outlined"
              @click="goBack"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              {{ $t('news.allNews') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, watch } from 'vue'  // 增加watch导入
import { newsArticles } from '@/data/news'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()

// 定义新闻数据
const newsId = ref<number>(0)
const newsTitle = ref<string>('')
const newsSummary = ref<string>('')
const newsContent = ref<string>('')
const newsCategoryKey = ref<string>('')
const newsCategory = ref<string>('')
const newsDate = ref<string>('')
const formattedDate = ref<string>('')
const newsImage = ref<string>('')
const isLoading = ref<boolean>(true)  // 添加加载状态
const error = ref<string>('')  // 添加错误状态

// Breadcrumbs
const breadcrumbs = ref([
  { title: t('nav.common.home'), href: '/' },
  { title: t('nav.news'), href: '/news' },
  { title: '', href: '' }
])

// Format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const currentLocale = locale.value
  return date.toLocaleDateString(currentLocale === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Get news data by ID
const getNewsData = () => {
  isLoading.value = true
  error.value = ''
  try {
    const id = Number(route.params.id)
    // 验证ID是否有效
    if (isNaN(id) || id < 1) {
      throw new Error('Invalid news ID')
    }
    newsId.value = id

    // 从新闻数据中查找对应的新闻
    const newsData = newsArticles.find(news => news.id === id)
    if (!newsData) {
      throw new Error('News article not found')
    }

    // 设置新闻数据
    newsTitle.value = newsData.title
    newsSummary.value = newsData.summary
    newsContent.value = newsData.content // 使用完整的内容
    newsCategoryKey.value = newsData.type
    newsCategory.value = t(`news.categories.${newsData.type}`)
    newsDate.value = newsData.date
    formattedDate.value = formatDate(newsData.date)
    newsImage.value = newsData.image

    // Set breadcrumb title
    breadcrumbs.value[2].title = newsTitle.value
    breadcrumbs.value[2].href = `/news/${id}`

  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load news data'
    console.error('Error loading news data:', err)
  } finally {
    isLoading.value = false
  }
}

// Go back to news list
const goBack = () => {
  router.push('/news')
}

// 监听语言变化
watch(locale, () => {
  getNewsData()
})

// 监听路由参数变化
watch(
  () => route.params.id,
  () => {
    getNewsData()
  },
  { immediate: true }
)

// 移除onMounted，因为watch已经有immediate: true
</script>

<style scoped>
/* Base Styles */
:root {
  --primary: #165DFF;
  --primary-light: #E8F3FF;
  --secondary: #36BFFA;
  --dark: #1D2939;
  --gray-800: #344054;
  --gray-600: #667085;
  --gray-400: #9CA3AF;
  --gray-200: #F9FAFB;
  --white: #FFFFFF;
}

.v-container:not(.fluid) {
  max-width: 1440px !important;
}

/* Typography */
h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  line-height: 1.2;
  color: var(--dark);
}

p {
  line-height: 1.6;
  color: var(--gray-600);
  margin-bottom: 1.5rem;
}

/* News Detail Header */
.news-detail-header {
  position: relative;
  overflow: hidden;
}

.news-detail-header-content {
  position: relative;
  z-index: 2;
  padding: 4rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.date-category {
  display: flex;
  align-items: center;
  font-size: 1rem;
}

/* Breadcrumbs */
.v-breadcrumbs {
  background-color: transparent !important;
  color: var(--white) !important;
}

.v-breadcrumbs-item {
  color: rgba(255, 255, 255, 0.8) !important;
}

.v-breadcrumbs-item:not(:last-child):after {
  color: rgba(255, 255, 255, 0.5) !important;
}

.v-breadcrumbs-item--active {
  color: var(--white) !important;
  font-weight: 600 !important;
}

/* News Detail Content */
.news-detail-content {
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--gray-800);
}

.news-detail-content h3 {
  font-size: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--dark);
}

.news-detail-content p {
  margin-bottom: 1.5rem;
}

.news-detail-content ul {
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
}

.news-detail-content li {
  margin-bottom: 0.5rem;
  list-style-type: disc;
}

/* Back to News Button */
.back-to-news {
  text-align: center;
}

.back-to-news .v-btn {
  border-color: var(--primary) !important;
  color: var(--primary) !important;
  border-radius: 8px !important;
  padding: 0.75rem 2rem !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.back-to-news .v-btn:hover {
  background-color: var(--primary-light) !important;
}
</style>